import Bugsnag from "@bugsnag/js";
import {
    IChangeEmployeeActiveStateRequestData,
    IChangeEmployeeRoleRequestData,
    IDeleteAccountRequestData,
    IKickCustomerRequestData,
    IKickEmployeeRequestData,
} from "@farmact/model/src/model/functions/accessManagementFunctionTypes";
import {
    ISetupCheckoutSessionRequestData,
    ISetupCheckoutSessionResponseData,
    ISetupCustomerPortalSessionRequestData,
    ISetupCustomerPortalSessionResponseData,
} from "@farmact/model/src/model/functions/billingFunctionTypes";
import {
    IAcceptInvitationRequestData,
    IAcceptInvitationResponseData,
    IAcceptMailInvitationRequestData,
    ICancelCustomerInvitationRequestData,
    ICancelEmployeeInvitationRequestData,
    IInvitationResponseData,
    IInviteCustomerRequestData,
    IInviteEmployeeRequestData,
    IRejectInvitationRequestData,
    IRejectInvitationResponseData,
} from "@farmact/model/src/model/functions/invitationFunctionTypes";
import {
    BaseMapStructuresSharingInsertResponse,
    BaseMapStructuresSharingUpdateResponse,
    BaseSharingRequest,
    GetCurrentlyActiveEmployeeLocationsForCustomerResponse,
    GetCurrentlyActiveOrderGeolocationTrackingsForCustomerResponse,
    GetFieldsByIdsForCustomerRequest,
    GetFieldsByIdsForCustomerResponse,
    GetMapStructuresDataForCustomerResponse,
    GetTracksByIdsForCustomerRequest,
    GetTracksByIdsForCustomerResponse,
    InsertFieldMarkersForCustomerRequest,
    InsertFieldsForCustomerRequest,
    InsertLoadingOrUnloadingPointsForCustomerRequest,
    InsertMarkersForCustomerRequest,
    InsertTracksForCustomerRequest,
    UpdateCustomerPositionForCustomerRequest,
    UpdateFieldForCustomerRequest,
    UpdateLoadingOrUnloadingPointForCustomerRequest,
    UpdateMarkerForCustomerRequest,
    UpdateTrackForCustomerRequest,
} from "@farmact/model/src/model/functions/sharingFunctionTypes";
import { SetAllCustomersMaschinenringSettingRequest } from "@farmact/model/src/model/functions/webAppFunctionTypes";
import {
    AnyArchiveOperatingUnitResponse,
    AnyCreateNewOperatingUnitResponse,
    ArchiveOperatingUnitRequest,
    CreateNewOperatingUnitRequest,
    IToggleCheckedOrdersRequestData,
} from "@farmact/model/src/model/functions/webAppFunctionTypes";
import { Role } from "@farmact/model/src/model/Role";
import { Functions, httpsCallable } from "firebase/functions";

export class FirebaseFunctions {
    private functions: Functions;

    constructor(parameters: { functions: Functions }) {
        this.functions = parameters.functions;
    }

    // FIXME: TODO: what is going on here
    public async inviteEmployee(inviteeMail: string, role: Role, employeeId: string): Promise<IInvitationResponseData> {
        return (
            await httpsCallable<IInviteEmployeeRequestData, IInvitationResponseData>(
                this.functions,
                "accessManagement-inviteUser"
            )({
                userMail: inviteeMail,
                employeeId,
            })
        ).data;
    }

    public async inviteCustomer(
        inviteeMail: string,
        customerId: string,
        usersCompanyId: string | undefined
    ): Promise<IInvitationResponseData> {
        if (!usersCompanyId) {
            Bugsnag.notify("No company id set when inviting customer");
            throw new Error("companyId not set");
        }
        return (
            await httpsCallable<IInviteCustomerRequestData, IInvitationResponseData>(
                this.functions,
                "accessManagement-inviteCustomer"
            )({
                customerMail: inviteeMail,
                companyId: usersCompanyId,
                customerId,
            })
        ).data;
    }

    public async acceptInvitation(invitationId: string) {
        return await httpsCallable<IAcceptInvitationRequestData, IAcceptInvitationResponseData>(
            this.functions,
            "accessManagement-acceptInvitation"
        )({
            invitationId,
        });
    }

    public async acceptMailInvitation(mailInvitationId: string, password: string) {
        return await httpsCallable<IAcceptMailInvitationRequestData, IAcceptInvitationResponseData>(
            this.functions,
            "accessManagement-acceptMailInvitation"
        )({
            mailInvitationId,
            password,
        });
    }

    public rejectInvitation(invitationId: string) {
        return httpsCallable<IRejectInvitationRequestData, IRejectInvitationResponseData>(
            this.functions,
            "accessManagement-rejectInvitation"
        )({ invitationId });
    }

    public cancelInvitation(invitedEmployeeId: string, usersCompanyId: string | undefined) {
        if (!usersCompanyId) {
            Bugsnag.notify("No company id set when cancelling employee invitation");
            throw new Error("companyId not set");
        }
        return httpsCallable<ICancelEmployeeInvitationRequestData>(
            this.functions,
            "accessManagement-cancelInvitation"
        )({
            invitingCompanyId: usersCompanyId,
            invitedEmployeeId,
        });
    }

    public cancelCustomerInvitation(invitedCustomerId: string, usersCompanyId: string | undefined) {
        if (!usersCompanyId) {
            Bugsnag.notify("No company id set when cancelling customer invitation");
            throw new Error("companyId not set");
        }
        return httpsCallable<ICancelCustomerInvitationRequestData>(
            this.functions,
            "accessManagement-cancelCustomerInvitation"
        )({
            invitingCompanyId: usersCompanyId,
            invitedCustomerId,
        });
    }

    public kickEmployee(employeeId: string, usersCompanyId: string | undefined) {
        if (!usersCompanyId) {
            Bugsnag.notify("No company id set when kicking employee");
            throw new Error("companyId not set");
        }
        return httpsCallable<IKickEmployeeRequestData>(
            this.functions,
            "accessManagement-kickEmployee"
        )({
            companyId: usersCompanyId,
            employeeId,
        });
    }

    public kickCustomer(customerId: string, usersCompanyId: string) {
        return httpsCallable<IKickCustomerRequestData>(
            this.functions,
            "accessManagement-kickCustomer"
        )({
            companyId: usersCompanyId,
            customerId,
        });
    }

    public deleteAccount(userId: string) {
        return httpsCallable<IDeleteAccountRequestData>(this.functions, "accessManagement-deleteAccount")({ userId });
    }

    public updateEmployeeRole(
        appUserId: string,
        employeeId: string,
        newRole: Role,
        usersCompanyId: string | undefined
    ) {
        if (!usersCompanyId) {
            Bugsnag.notify("No company id set when updating employee role");
            throw new Error("companyId not set");
        }
        return httpsCallable<IChangeEmployeeRoleRequestData>(
            this.functions,
            "accessManagement-changeEmployeeRole"
        )({
            companyId: usersCompanyId,
            employeeId,
            appUserId,
            newRole,
        });
    }

    public setupCheckoutSession(usersCompanyId: string | undefined) {
        if (!usersCompanyId) {
            Bugsnag.notify("No company id set when setting up checkout session");
            throw new Error("companyId not set");
        }
        return httpsCallable<ISetupCheckoutSessionRequestData, ISetupCheckoutSessionResponseData>(
            this.functions,
            "billing-setupCheckoutSession"
        )({
            companyId: usersCompanyId,
        });
    }

    public setupCustomerPortalSession(usersCompanyId: string | undefined) {
        if (!usersCompanyId) {
            Bugsnag.notify("No company id set when setting up customer portal session");
            throw new Error("companyId not set");
        }
        return httpsCallable<ISetupCustomerPortalSessionRequestData, ISetupCustomerPortalSessionResponseData>(
            this.functions,
            "billing-setupCustomerPortalSession"
        )({
            companyId: usersCompanyId,
        });
    }

    public setAllCustomersMaschinenringSetting(request: SetAllCustomersMaschinenringSettingRequest) {
        return httpsCallable<SetAllCustomersMaschinenringSettingRequest, void>(
            this.functions,
            "webApp-setAllCustomersMaschinenringSetting"
        )(request);
    }

    public createNewOperatingUnit(request: CreateNewOperatingUnitRequest) {
        return httpsCallable<CreateNewOperatingUnitRequest, AnyCreateNewOperatingUnitResponse>(
            this.functions,
            "webApp-createNewOperatingUnit"
        )(request);
    }

    public archiveOperatingUnit(request: ArchiveOperatingUnitRequest) {
        return httpsCallable<ArchiveOperatingUnitRequest, AnyArchiveOperatingUnitResponse>(
            this.functions,
            "webApp-archiveOperatingUnit"
        )(request);
    }

    public changeEmployeeActiveState(data: IChangeEmployeeActiveStateRequestData) {
        return httpsCallable<IChangeEmployeeActiveStateRequestData>(
            this.functions,
            "accessManagement-changeEmployeeActiveState"
        )(data);
    }

    public toggleCheckedOrders(data: IToggleCheckedOrdersRequestData) {
        return httpsCallable<IToggleCheckedOrdersRequestData>(this.functions, "webApp-toggleCheckedOrders")(data);
    }

    public getCurrentlyActiveOrderGeolocationTrackingsForCustomer(data: BaseSharingRequest) {
        return httpsCallable<BaseSharingRequest, GetCurrentlyActiveOrderGeolocationTrackingsForCustomerResponse>(
            this.functions,
            "sharing-getCurrentlyActiveOrderGeolocationTrackingsForCustomer"
        )(data);
    }

    public getCurrentlyActiveEmployeeLocationsForCustomer(data: BaseSharingRequest) {
        return httpsCallable<BaseSharingRequest, GetCurrentlyActiveEmployeeLocationsForCustomerResponse>(
            this.functions,
            "sharing-getCurrentlyActiveEmployeeLocationsForCustomer"
        )(data);
    }

    public getFieldsDataForCustomer(data: BaseSharingRequest) {
        return httpsCallable<BaseSharingRequest, GetMapStructuresDataForCustomerResponse>(
            this.functions,
            "sharing-getFieldsDataForCustomer"
        )(data);
    }

    public getFieldsByIdsForCustomer(data: GetFieldsByIdsForCustomerRequest) {
        return httpsCallable<GetFieldsByIdsForCustomerRequest, GetFieldsByIdsForCustomerResponse>(
            this.functions,
            "sharing-getFieldsByIdsForCustomer"
        )(data);
    }

    public getTracksByIdsForCustomer(data: GetTracksByIdsForCustomerRequest) {
        return httpsCallable<GetTracksByIdsForCustomerRequest, GetTracksByIdsForCustomerResponse>(
            this.functions,
            "sharing-getTracksByIdsForCustomer"
        )(data);
    }

    public updateCustomerPositionForCustomer(data: UpdateCustomerPositionForCustomerRequest) {
        return httpsCallable<UpdateCustomerPositionForCustomerRequest, BaseMapStructuresSharingUpdateResponse>(
            this.functions,
            "sharing-updateCustomerPositionForCustomer"
        )(data);
    }

    public updateFieldForCustomer(data: UpdateFieldForCustomerRequest) {
        return httpsCallable<UpdateFieldForCustomerRequest, BaseMapStructuresSharingUpdateResponse>(
            this.functions,
            "sharing-updateFieldForCustomer"
        )(data);
    }

    public updateTrackForCustomer(data: UpdateTrackForCustomerRequest) {
        return httpsCallable<UpdateTrackForCustomerRequest, BaseMapStructuresSharingUpdateResponse>(
            this.functions,
            "sharing-updateTrackForCustomer"
        )(data);
    }

    public updateLoadingOrUnloadingPointForCustomer(data: UpdateLoadingOrUnloadingPointForCustomerRequest) {
        return httpsCallable<UpdateLoadingOrUnloadingPointForCustomerRequest, BaseMapStructuresSharingUpdateResponse>(
            this.functions,
            "sharing-updateLoadingOrUnloadingPointForCustomer"
        )(data);
    }

    public updateMarkerForCustomer(data: UpdateMarkerForCustomerRequest) {
        return httpsCallable<UpdateMarkerForCustomerRequest, BaseMapStructuresSharingUpdateResponse>(
            this.functions,
            "sharing-updateMarkerForCustomer"
        )(data);
    }

    public insertFieldsForCustomer(data: InsertFieldsForCustomerRequest) {
        return httpsCallable<InsertFieldsForCustomerRequest, BaseMapStructuresSharingInsertResponse>(
            this.functions,
            "sharing-insertFieldsForCustomer"
        )(data);
    }

    public insertTracksForCustomer(data: InsertTracksForCustomerRequest) {
        return httpsCallable<InsertTracksForCustomerRequest, BaseMapStructuresSharingInsertResponse>(
            this.functions,
            "sharing-insertTracksForCustomer"
        )(data);
    }

    public insertCustomerMarkersForCustomer(data: InsertMarkersForCustomerRequest) {
        return httpsCallable<InsertMarkersForCustomerRequest, BaseMapStructuresSharingInsertResponse>(
            this.functions,
            "sharing-insertCustomerMarkersForCustomer"
        )(data);
    }

    public insertFieldMarkersForCustomer(data: InsertFieldMarkersForCustomerRequest) {
        return httpsCallable<InsertFieldMarkersForCustomerRequest, BaseMapStructuresSharingInsertResponse>(
            this.functions,
            "sharing-insertFieldMarkersForCustomer"
        )(data);
    }

    public insertLoadingOrUnloadingPointsForCustomer(data: InsertLoadingOrUnloadingPointsForCustomerRequest) {
        return httpsCallable<InsertLoadingOrUnloadingPointsForCustomerRequest, BaseMapStructuresSharingInsertResponse>(
            this.functions,
            "sharing-insertLoadingOrUnloadingPointsForCustomer"
        )(data);
    }
}
